// Install state: where the app runs, whether it can be installed here, and how.
// - beforeinstallprompt (Chrome/Edge on Android and desktop) is captured as soon as this module
//   loads (it is imported by the shell at start-up) and kept for the "Asenna" button.
// - "Installed" = running standalone (display-mode or iOS navigator.standalone), or the browser
//   said so (appinstalled, getInstalledRelatedApps), or this device remembers an install.
// - iOS has no install prompt: the Install page shows the Share → "Lisää Koti-valikkoon" steps.
// The banner (InstallBanner) is quiet and one-time: on first launch in a browser tab and in
// "Lisää" until dismissed; never inside the installed app.
import { useSyncExternalStore } from 'react';

export type Platform =
  | 'ios-safari' // iPhone/iPad Safari: Share → Add to Home Screen
  | 'ios-other' // Chrome/Edge/Firefox on iOS 16.4+: Share → Add to Home Screen as well
  | 'android' // Chromium on Android: prompt or menu → Install app
  | 'desktop' // Chrome/Edge on Windows/macOS/Linux: prompt or the install icon in the address bar
  | 'other'; // Firefox desktop, Safari on macOS, …: explained, no button

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface InstallState {
  platform: Platform;
  standalone: boolean;
  installed: boolean;
  canPrompt: boolean;
  /** Banner dismissed for good on this device. */
  dismissed: boolean;
}

const K_INSTALLED = 'valyrio:installed';
const K_DISMISSED = 'valyrio:installHint';
const K_FIRST = 'valyrio:firstLaunchSeen';

function lsGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function lsSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    // private mode: the banner may show again, nothing worse
  }
}

export function detectPlatform(ua = typeof navigator === 'undefined' ? '' : navigator.userAgent): Platform {
  const iPadOS = typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) {
    return /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua) ? 'ios-other' : 'ios-safari';
  }
  const chromium = /Chrome\/|Chromium\/|Edg\//.test(ua) && !/OPR\/|SamsungBrowser/.test(ua);
  if (/Android/.test(ua)) return chromium || /SamsungBrowser/.test(ua) ? 'android' : 'other';
  return chromium ? 'desktop' : 'other';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const n = window.navigator as Navigator & { standalone?: boolean };
  if (n.standalone === true) return true;
  const mm = window.matchMedia?.bind(window);
  return !!mm && ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay'].some((m) => mm(`(display-mode: ${m})`).matches);
}

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallState = {
  platform: detectPlatform(),
  standalone: isStandalone(),
  installed: isStandalone() || lsGet(K_INSTALLED) === '1',
  canPrompt: false,
  dismissed: lsGet(K_DISMISSED) === 'dismissed',
};
const listeners = new Set<() => void>();
function set(p: Partial<InstallState>) {
  state = { ...state, ...p };
  listeners.forEach((l) => l());
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Keep Chrome's mini-infobar away: the app offers install itself, quietly.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    // The browser only offers this when the app is NOT installed (here, for this profile).
    lsSet(K_INSTALLED, '0');
    set({ canPrompt: true, installed: isStandalone() });
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    lsSet(K_INSTALLED, '1');
    set({ canPrompt: false, installed: true });
  });
  // The window may move between a tab and the app window (Chrome "Open in app").
  window.matchMedia?.('(display-mode: standalone)').addEventListener?.('change', () => {
    const standalone = isStandalone();
    set({ standalone, installed: standalone || state.installed });
  });
  // Chromium (Android, and desktop for same-origin web apps) can tell a tab that the app is
  // installed, via manifest related_applications.
  const n = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<Array<{ platform: string }>> };
  if (!state.standalone && typeof n.getInstalledRelatedApps === 'function') {
    n.getInstalledRelatedApps()
      .then((apps) => {
        if (apps.some((a) => a.platform === 'webapp')) {
          lsSet(K_INSTALLED, '1');
          set({ installed: true });
        }
      })
      .catch(() => undefined);
  }
}

export const installStore = {
  get: (): InstallState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useInstall(): InstallState {
  return useSyncExternalStore(installStore.subscribe, installStore.get, installStore.get);
}

/** Show the browser's install dialog (Chromium). Resolves to the user's choice. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = deferred;
  if (!e) return 'unavailable';
  deferred = null;
  set({ canPrompt: false });
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    if (outcome === 'accepted') {
      lsSet(K_INSTALLED, '1');
      set({ installed: true });
    }
    return outcome;
  } catch {
    return 'unavailable';
  }
}

export function dismissInstallHint(): void {
  lsSet(K_DISMISSED, 'dismissed');
  set({ dismissed: true });
}

/** True when installing here makes sense at all (a browser tab on a platform that can install). */
export function installable(s: InstallState): boolean {
  if (s.standalone || s.installed) return false;
  return s.platform === 'ios-safari' || s.platform === 'ios-other' || s.canPrompt || s.platform === 'android' || s.platform === 'desktop';
}

// First launch in a browser tab: decided once per page load, then remembered.
const firstLaunch = (() => {
  if (typeof window === 'undefined') return false;
  const seen = lsGet(K_FIRST) === '1';
  if (!seen && !state.standalone) lsSet(K_FIRST, '1');
  return !seen;
})();

/** The shell's banner: only on the first launch in a browser tab, until dismissed. */
export function showFirstLaunchBanner(s: InstallState): boolean {
  return firstLaunch && !s.dismissed && installable(s);
}

/** The "Lisää" banner: in a browser tab, until dismissed. */
export function showMoreBanner(s: InstallState): boolean {
  return !s.dismissed && installable(s);
}
